import { get as httpGet, put as httpPut } from '@/services/http';

export const CANONICAL_COLUMN_IDS = [
  'poNumber', 'supplier', 'location', 'source', 'status', 'orderDate',
  'departureDate', 'expectedDeliveryDate', 'total', 'paymentStatus',
  'paymentDueDate', 'internalNotes', 'actions',
  // Financial detail columns — hidden by default (see DEFAULT_VISIBLE_COLUMNS).
  'invoiceNumber', 'subtotal', 'itemTax', 'shippingCost', 'shippingTax',
  'discount', 'adjustment', 'taxTotal', 'grandTotal',
] as const;

export const LOCKED_COLUMN_IDS = ['poNumber', 'supplier', 'status', 'actions'];

// departureDate and the financial detail columns start hidden — departureDate
// per the user-feedback-driven default swap (US-084), the financial columns
// because showing all 9 by default would take the table from 12 to 21 columns.
const HIDDEN_BY_DEFAULT = [
  'departureDate', 'invoiceNumber', 'subtotal', 'itemTax', 'shippingCost',
  'shippingTax', 'discount', 'adjustment', 'taxTotal', 'grandTotal',
];
export const DEFAULT_VISIBLE_COLUMNS = CANONICAL_COLUMN_IDS.filter((id) => !HIDDEN_BY_DEFAULT.includes(id));

const COLUMN_LABELS: Record<string, string> = {
  location: 'Delivery Location',
  source: 'Source',
  orderDate: 'Order Date',
  departureDate: 'Departure',
  expectedDeliveryDate: 'Expected Delivery',
  total: 'Total',
  paymentStatus: 'Payment',
  paymentDueDate: 'Payment Due',
  internalNotes: 'Internal Note',
  invoiceNumber: 'Invoice Number',
  subtotal: 'Subtotal',
  itemTax: 'Item Tax',
  shippingCost: 'Shipping',
  shippingTax: 'Shipping Tax',
  discount: 'Discount',
  adjustment: 'Adjustment',
  taxTotal: 'Total Tax',
  grandTotal: 'Grand Total',
};

// Derived from CANONICAL_COLUMN_IDS/LOCKED_COLUMN_IDS so a future canonical
// column can't be added without also becoming toggleable (or explicitly locked).
export const TOGGLEABLE_COLUMN_LABELS: { id: string; label: string }[] = CANONICAL_COLUMN_IDS
  .filter((id) => !LOCKED_COLUMN_IDS.includes(id))
  .map((id) => ({ id, label: COLUMN_LABELS[id] }));

// Canonical id -> this app's `headers[].value`. Ids not listed map 1:1
// (poNumber, source, status, orderDate, departureDate, expectedDeliveryDate,
// paymentStatus, paymentDueDate, internalNotes, actions).
export const WARBLER_COLUMN_VALUE_MAP: Record<string, string> = {
  supplier: 'supplierName',
  location: 'locationName',
  total: 'totalAmount',
};

const HEADER_VALUE_TO_CANONICAL_ID: Record<string, string> = Object.fromEntries(
  Object.entries(WARBLER_COLUMN_VALUE_MAP).map(([canonicalId, headerValue]) => [headerValue, canonicalId]),
);

function canonicalIdForHeaderValue(headerValue: string): string {
  return HEADER_VALUE_TO_CANONICAL_ID[headerValue] ?? headerValue;
}

export function filterHeaders<T extends { value: string }>(allHeaders: T[], visibleColumns: string[]): T[] {
  return allHeaders.filter((header) => {
    const canonicalId = canonicalIdForHeaderValue(header.value);
    return LOCKED_COLUMN_IDS.includes(canonicalId) || visibleColumns.includes(canonicalId);
  });
}

interface GetDep { get: (url: string, params?: any) => Promise<any>; }
interface PutDep { put: (url: string, body?: any) => Promise<any>; }

export async function fetchListColumnsConfig(deps: GetDep = { get: httpGet }): Promise<string[]> {
  try {
    const response: any = await deps.get('/purchase_orders/config/list-columns');
    const visibleColumns = response?.data?.visibleColumns;
    return Array.isArray(visibleColumns) ? visibleColumns : [...DEFAULT_VISIBLE_COLUMNS];
  } catch {
    return [...DEFAULT_VISIBLE_COLUMNS];
  }
}

export async function saveListColumnsConfig(visibleColumns: string[], deps: PutDep = { put: httpPut }): Promise<void> {
  await deps.put('/purchase_orders/config/list-columns', { visibleColumns });
}
